import Api from '../api';
import { OcorrenciasResponse } from './ai_management_hiring_history';
import { cachedGet, peekGet } from '../request_cache';

export type PossibleCompetitorsOrigin = 'edital' | 'historico';

export interface PossibleCompetitorsItemResponse {
  key: string;
  label: string;
  catalogo: string | null;
  descricao: string | null;
  escopo: 'estado' | 'brasil';
  interesse: boolean;
  empresas: number;
  licitacoes: number;
  media_preco: number | null;
}

export interface PossibleCompetitorsResponse {
  estado: string | null;
  origem: PossibleCompetitorsOrigin;
  periodo: number;
  total: number;
  page: number;
  per_page: number;
  items: PossibleCompetitorsItemResponse[];
}

export interface PossibleCompetitorsParams {
  page?: number;
  perPage?: number;
  search?: string;
  period?: number;
}

const listRequest = (biddingId: number, params: PossibleCompetitorsParams) =>
  [
    `/gestao_ia/licitacoes/${biddingId}/possiveis_concorrentes`,
    {
      params: {
        page: params.page,
        per_page: params.perPage,
        q: params.search || undefined,
        periodo: params.period,
      },
    },
  ] as const;

export const listPossibleCompetitors = (
  biddingId: number,
  params: PossibleCompetitorsParams = {},
) => cachedGet<PossibleCompetitorsResponse>(...listRequest(biddingId, params));

export const peekPossibleCompetitors = (
  biddingId: number,
  params: PossibleCompetitorsParams = {},
) => peekGet<PossibleCompetitorsResponse>(...listRequest(biddingId, params));

export interface PossibleCompetitorCompanyResponse {
  cnpj: string;
  nome: string | null;
  media_preco: number | null;
  participacoes: number;
  ganhas: number;
  desclassificadas: number;
  deste_orgao: number;
}

export interface PossibleCompetitorCompaniesResponse {
  item: string;
  items: PossibleCompetitorCompanyResponse[];
}

const companiesRequest = (biddingId: number, item: string, period: number) =>
  [
    `/gestao_ia/licitacoes/${biddingId}/possiveis_concorrentes/empresas`,
    { params: { item, periodo: period } },
  ] as const;

export const listPossibleCompetitorCompanies = (biddingId: number, item: string, period: number) =>
  cachedGet<PossibleCompetitorCompaniesResponse>(...companiesRequest(biddingId, item, period));

export const peekPossibleCompetitorCompanies = (biddingId: number, item: string, period: number) =>
  peekGet<PossibleCompetitorCompaniesResponse>(...companiesRequest(biddingId, item, period));

export interface PossibleCompetitorParticipation {
  numero_lote: string | null;
  numero_item: string | null;
  label: string | null;
  descricao: string | null;
  marca: string | null;
  modelo: string | null;
  quantidade: number | null;
  valor_unitario: number | null;
  posicao: string | null;
  proposta_inicial: number | null;
  lance_final: number | null;
  valor_final: number | null;
  adjudicado: boolean;
  desclassificada: boolean;
  inabilitada: boolean;
  motivo: string | null;
  me_epp: boolean;
}

export interface PossibleCompetitorBidding {
  bidding_id: number;
  edital: string | null;
  processo: string | null;
  objeto: string | null;
  orgao_id: number | null;
  orgao: string | null;
  cidade: string | null;
  data: string | null;
  valor_estimado: number | null;
  latitude?: number | null;
  longitude?: number | null;
  itens: PossibleCompetitorParticipation[];
}

export interface PossibleCompetitorCompanyBiddingsResponse {
  empresa: { cnpj: string; nome: string | null };
  item: string;
  licitacoes: PossibleCompetitorBidding[];
}

const companyRequest = (biddingId: number, item: string, cnpj: string, period: number) =>
  [
    `/gestao_ia/licitacoes/${biddingId}/possiveis_concorrentes/empresa`,
    { params: { item, cnpj, periodo: period } },
  ] as const;

export const getPossibleCompetitorCompany = (
  biddingId: number,
  item: string,
  cnpj: string,
  period: number,
) =>
  cachedGet<PossibleCompetitorCompanyBiddingsResponse>(
    ...companyRequest(biddingId, item, cnpj, period),
  );

export const peekPossibleCompetitorCompany = (
  biddingId: number,
  item: string,
  cnpj: string,
  period: number,
) =>
  peekGet<PossibleCompetitorCompanyBiddingsResponse>(
    ...companyRequest(biddingId, item, cnpj, period),
  );

export interface PossibleCompetitorEditalItem {
  numero_item: string;
  descricao: string | null;
  quantidade: number | null;
  unidade: string | null;
  valor_unitario: number | null;
  valor_estimado: number | null;
}

export interface PossibleCompetitorDetailResponse {
  licitacao: Omit<PossibleCompetitorBidding, 'itens'>;
  edital: PossibleCompetitorEditalItem[];
  participacoes: PossibleCompetitorParticipation[];
}

const detailRequest = (biddingId: number, historicBiddingId: number, item: string, cnpj: string) =>
  [
    `/gestao_ia/licitacoes/${biddingId}/possiveis_concorrentes/detalhe`,
    { params: { bidding: historicBiddingId, item, cnpj } },
  ] as const;

export const getPossibleCompetitorDetail = (
  biddingId: number,
  historicBiddingId: number,
  item: string,
  cnpj: string,
) =>
  cachedGet<PossibleCompetitorDetailResponse>(
    ...detailRequest(biddingId, historicBiddingId, item, cnpj),
  );

export const peekPossibleCompetitorDetail = (
  biddingId: number,
  historicBiddingId: number,
  item: string,
  cnpj: string,
) =>
  peekGet<PossibleCompetitorDetailResponse>(
    ...detailRequest(biddingId, historicBiddingId, item, cnpj),
  );

export interface PossibleCompetitorReason {
  texto: string;
  itens: number;
  licitacoes: number;
}

export interface PossibleCompetitorReasons {
  texto: string | null;
  motivos: PossibleCompetitorReason[];
  sem_motivo_itens: number;
  outros_motivos: number;
  outros_itens: number;
}

export interface PossibleCompetitorQualification {
  texto: string | null;
  desclassificados: number;
  inabilitados: number;
  desclassificadas: PossibleCompetitorReasons;
  inabilitadas: PossibleCompetitorReasons;
}

export interface PossibleCompetitorRankingDistribution {
  primeiro: number;
  segundo_terceiro: number;
  quarto_decimo: number;
  acima_decimo: number;
  sem_classificacao: number;
}

export interface PossibleCompetitorPricing {
  texto: string | null;
  pct_primeiro: number | null;
  pct_top3: number | null;
  com_classificacao: number;
  distribuicao: PossibleCompetitorRankingDistribution;
}

export interface PossibleCompetitorExperienceCounts {
  licitacoes: number;
  adjudicados: number;
  desclassificados: number;
  inabilitados: number;
}

export interface PossibleCompetitorPublicBodyExperience extends PossibleCompetitorExperienceCounts {
  id: number | null;
  nome: string;
}

export interface PossibleCompetitorStateExperience extends PossibleCompetitorExperienceCounts {
  uf: string;
}

export interface PossibleCompetitorSizeYear {
  ano: number;
  completo: boolean;
  ate_mes: number | null;
  valor: number | null;
}

export interface PossibleCompetitorContractedYear extends PossibleCompetitorSizeYear {
  licitacoes: number[];
  acima_limite: boolean;
}

export interface PossibleCompetitorSize {
  texto: string | null;
  adjudicados: PossibleCompetitorSizeYear[];
  contratados: PossibleCompetitorContractedYear[];
}

export interface PossibleCompetitorAnalysis {
  cnpj: string;
  razao_social: string | null;
  me_epp: boolean;
  resumo: {
    licitacoes: number;
    itens: number;
    adjudicados: number;
    desclassificados: number;
    inabilitados: number;
    taxa_adjudicacao: number | null;
  };
  analise: {
    resumo_geral: string | null;
    pontos_fortes: string[];
    pontos_de_atencao: string[];
  } | null;
  habilitacao: PossibleCompetitorQualification;
  preco_competitivo: PossibleCompetitorPricing;
  por_orgao: { texto: string | null; dados: PossibleCompetitorPublicBodyExperience[] };
  por_uf: { texto: string | null; dados: PossibleCompetitorStateExperience[] };
  porte: PossibleCompetitorSize;
  desatualizada: boolean;
  gerado_em: string | null;
}

export interface PossibleCompetitorAnalysisResponse {
  pendente: boolean;
  analise: PossibleCompetitorAnalysis | null;
}

export const POSSIBLE_COMPETITOR_ANALYSIS_PATH = '/possiveis_concorrentes/analise';

const analysisRequest = (biddingId: number, cnpj: string) =>
  [
    `/gestao_ia/licitacoes/${biddingId}${POSSIBLE_COMPETITOR_ANALYSIS_PATH}`,
    { params: { cnpj } },
  ] as const;

export const getPossibleCompetitorAnalysis = (biddingId: number, cnpj: string) =>
  cachedGet<PossibleCompetitorAnalysisResponse>(...analysisRequest(biddingId, cnpj));

export const peekPossibleCompetitorAnalysis = (biddingId: number, cnpj: string) =>
  peekGet<PossibleCompetitorAnalysisResponse>(...analysisRequest(biddingId, cnpj));

export interface PossibleCompetitorSanction {
  id: number | null;
  categoria: string | null;
  portal: string | null;
  orgao: string | null;
  inicio: string | null;
  fim: string | null;
  vigente: boolean;
  url: string | null;
}

export interface PossibleCompetitorSanctionsResponse {
  penalidades: PossibleCompetitorSanction[];
}

const sanctionsRequest = (biddingId: number, cnpj: string) =>
  [
    `/gestao_ia/licitacoes/${biddingId}/possiveis_concorrentes/penalidades`,
    { params: { cnpj } },
  ] as const;

export const getPossibleCompetitorSanctions = (biddingId: number, cnpj: string) =>
  cachedGet<PossibleCompetitorSanctionsResponse>(...sanctionsRequest(biddingId, cnpj));

export const peekPossibleCompetitorSanctions = (biddingId: number, cnpj: string) =>
  peekGet<PossibleCompetitorSanctionsResponse>(...sanctionsRequest(biddingId, cnpj));

export interface PossibleCompetitorContractFile {
  id: number | null;
  titulo: string;
  tipo: string | null;
  data: string | null;
  url: string;
}

export interface PossibleCompetitorContract {
  id: number | null;
  numero: string | null;
  fornecedor: string | null;
  valor: number | null;
  assinatura: string | null;
  vigencia_inicio: string | null;
  vigencia_fim: string | null;
  fonte: string | null;
  arquivos: PossibleCompetitorContractFile[];
}

export interface PossibleCompetitorContractsResponse {
  contratos: PossibleCompetitorContract[];
}

const contractsRequest = (biddingId: number, historicBiddingId: number, cnpj: string) =>
  [
    `/gestao_ia/licitacoes/${biddingId}/possiveis_concorrentes/contratos`,
    { params: { bidding: historicBiddingId, cnpj } },
  ] as const;

export const getPossibleCompetitorContracts = (
  biddingId: number,
  historicBiddingId: number,
  cnpj: string,
) =>
  cachedGet<PossibleCompetitorContractsResponse>(
    ...contractsRequest(biddingId, historicBiddingId, cnpj),
  );

export const peekPossibleCompetitorContracts = (
  biddingId: number,
  historicBiddingId: number,
  cnpj: string,
) =>
  peekGet<PossibleCompetitorContractsResponse>(
    ...contractsRequest(biddingId, historicBiddingId, cnpj),
  );

export interface PossibleCompetitorDocument {
  id: string;
  nome: string;
  arquivo: string;
  tamanho: number | null;
  data: string | null;
  download_url: string;
}

export interface PossibleCompetitorDocumentsResponse {
  documentos: PossibleCompetitorDocument[];
}

const documentsRequest = (biddingId: number, historicBiddingId: number, cnpj: string) =>
  [
    `/gestao_ia/licitacoes/${biddingId}/possiveis_concorrentes/documentos`,
    { params: { bidding: historicBiddingId, cnpj } },
  ] as const;

export const listPossibleCompetitorDocuments = (
  biddingId: number,
  historicBiddingId: number,
  cnpj: string,
) =>
  cachedGet<PossibleCompetitorDocumentsResponse>(
    ...documentsRequest(biddingId, historicBiddingId, cnpj),
  );

export const peekPossibleCompetitorDocuments = (
  biddingId: number,
  historicBiddingId: number,
  cnpj: string,
) =>
  peekGet<PossibleCompetitorDocumentsResponse>(
    ...documentsRequest(biddingId, historicBiddingId, cnpj),
  );

export const listPossibleCompetitorOcorrencias = (
  biddingId: number,
  historicBiddingId: number,
  cnpj: string,
) =>
  Api.get<OcorrenciasResponse>(
    `/gestao_ia/licitacoes/${biddingId}/possiveis_concorrentes/ocorrencias`,
    { params: { bidding: historicBiddingId, cnpj } },
  );

export const downloadPossibleCompetitorOcorrenciaZip = (
  biddingId: number,
  historicBiddingId: number,
  cnpj: string,
  indice: number,
) =>
  Api.get<Blob>(`/gestao_ia/licitacoes/${biddingId}/possiveis_concorrentes/ocorrencias/arquivos`, {
    params: { bidding: historicBiddingId, cnpj, indice },
    responseType: 'blob',
  });

export interface PossibleCompetitorCompanyContract extends PossibleCompetitorContract {
  ano: number | null;
  licitacao: {
    id: number | null;
    edital: string | null;
    orgao: string | null;
    cidade: string | null;
  };
}

export interface PossibleCompetitorCompanyContractsResponse {
  contratos: PossibleCompetitorCompanyContract[];
}

const companyContractsRequest = (biddingId: number, cnpj: string, biddingIds: number[]) =>
  [
    `/gestao_ia/licitacoes/${biddingId}/possiveis_concorrentes/contratos_empresa`,
    { params: { cnpj, licitacoes: biddingIds.length ? biddingIds.join(',') : undefined } },
  ] as const;

export const getPossibleCompetitorCompanyContracts = (
  biddingId: number,
  cnpj: string,
  biddingIds: number[],
) =>
  cachedGet<PossibleCompetitorCompanyContractsResponse>(
    ...companyContractsRequest(biddingId, cnpj, biddingIds),
  );

export const peekPossibleCompetitorCompanyContracts = (
  biddingId: number,
  cnpj: string,
  biddingIds: number[],
) =>
  peekGet<PossibleCompetitorCompanyContractsResponse>(
    ...companyContractsRequest(biddingId, cnpj, biddingIds),
  );
